'use client'

import { useRouter } from 'next/navigation'
import { useTheme } from '@/contexts/ThemeContext'

/* ── 데이터 ─────────────────────────────────── */

const DM_PAINS = [
  { emoji: '😮‍💨', text: '"강사님 저 입금했어요~" — 하루 종일 통장 새로고침' },
  { emoji: '📏', text: '"수트 사이즈요? 핀 사이즈는요? 카풀 어디서 타실래요?" — DM 30번' },
  { emoji: '😤', text: '노쇼 당해도 입금 요청하기 뻘쭘 — 그냥 손해 봄' },
  { emoji: '🗂️', text: '구글폼 + 엑셀 + 카톡 = 3개 창 왔다갔다 하며 수작업' },
]

const DM_AFTERS = [
  { emoji: '✅', text: '결제 순간 수트·핀·렌탈·카풀 정보 대시보드에 자동 정리 — DM 0건' },
  { emoji: '🛡️', text: '9,900원 낸 수강생 = 진지한 사람. 노쇼율 극적 하락' },
  { emoji: '🏅', text: 'AIDA/PADI 공식 인증 뱃지 + 실시간 잔여석 — 링크트리가 못 주는 신뢰' },
  { emoji: '🚀', text: '관리 시간 0 → 강의 준비에만 집중' },
]

const VALUES = [
  {
    icon: '🛡️',
    title: '노쇼 원천 차단',
    desc: '9,900원 예약금을 낸 수강생은 나타납니다. 카톡으로 "올게요~" 한 사람과 다릅니다.',
  },
  {
    icon: '🤖',
    title: 'DM 노가다 해방',
    desc: '결제와 동시에 수트·핀 사이즈, 장비 렌탈, 카풀 수요가 대시보드에 자동 취합됩니다.',
  },
  {
    icon: '🏅',
    title: '프로 아카데미 격상',
    desc: 'AIDA/PADI 인증 뱃지 + 실시간 잔여석 UI. 링크트리 버튼과 차원이 다른 신뢰감.',
  },
]

const STEPS = [
  {
    num: '1',
    title: '강사 등록 (5분)',
    desc: '자격증 정보 입력, 심사 없음. 즉시 AIDA/PADI 인증 뱃지 프로필 생성.',
    badge: '무료',
  },
  {
    num: '2',
    title: '투어 페이지 공개',
    desc: '날짜·인원·수집 항목(사이즈, 렌탈 등)을 설정하면 예약 페이지 자동 완성.',
    badge: '첫 투어 무료',
  },
  {
    num: '3',
    title: '대시보드에서 확인 끝',
    desc: '신청자 명단, 수트·핀 사이즈, 렌탈 여부, 카풀 위치가 한 화면에 정리됩니다.',
    badge: '자동화',
  },
]

const FAQ = [
  {
    q: '링크트리나 인스타 DM이랑 뭐가 다른가요?',
    a: '링크트리는 링크만 연결합니다. 오늘다이빙은 예약금 결제 → 신청자 정보 취합 → 명단 관리까지 자동으로 연결됩니다. 카톡 DM이 필요 없어집니다.',
  },
  {
    q: '수강생이 내는 9,900원은 강사한테 가나요?',
    a: '네. 수강생의 9,900원 예약금은 강사님 계좌로 직접 입금됩니다. 플랫폼이 한 푼도 가져가지 않습니다.',
  },
  {
    q: '첫 번째 투어가 진짜 무료인가요?',
    a: '네. 첫 투어는 이용료 없이 즉시 공개됩니다. 두 번째 투어부터 건당 9,900원.',
  },
  {
    q: '자격증이 꼭 있어야 하나요?',
    a: 'AIDA, SSI, PADI, Molchanovs 등 공인 프리다이빙 강사 자격증이 필요합니다.',
  },
]

/* ── 컴포넌트 ─────────────────────────────────── */

export default function ForInstructorsPage() {
  const router = useRouter()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const bg      = isDark ? '#0f1724' : '#ffffff'
  const ink     = isDark ? '#f1f5fb' : '#0a1929'
  const sub     = isDark ? '#8b99b5' : '#6b7684'
  const card    = isDark ? '#161f2e' : '#f7f8fa'
  const border  = isDark ? '#1e2d42' : '#eaecf0'
  const blue    = '#3182f6'
  const blueDim = isDark ? '#1e3a5f' : '#ebf3ff'
  const blueText= isDark ? '#60a5fa' : '#1d4ed8'

  return (
    <div style={{ background: bg, minHeight: '100vh' }}>

      {/* ━━ HERO ━━ */}
      <section className="px-5 pt-12 pb-10 max-w-xl mx-auto">

        {/* 공감 태그 */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-6"
          style={{ background: isDark ? '#1e1a0e' : '#fffbeb', border: `1px solid ${isDark ? '#3d3010' : '#fde68a'}` }}>
          <span style={{ fontSize: 13 }}>📱</span>
          <span style={{ font: '500 12px/1 sans-serif', color: isDark ? '#fcd34d' : '#92400e' }}>
            지금도 카톡 DM으로 예약 받으시나요?
          </span>
        </div>

        {/* 헤드라인 */}
        <h1 style={{ font: '800 28px/36px sans-serif', color: ink, margin: '0 0 14px', letterSpacing: '-0.04em' }}>
          인스타 DM으로<br />
          언제까지 혼자<br />
          다 하실 건가요?
        </h1>
        <p style={{ font: '400 15px/24px sans-serif', color: sub, margin: '0 0 28px' }}>
          입금 확인, 사이즈 묻기, 노쇼 스트레스...<br />
          수강생이 늘수록 <span style={{ color: isDark ? '#f87171' : '#e11d48', fontWeight: 600 }}>행정 노가다도 같이 늘어납니다.</span>
        </p>

        <button
          onClick={() => router.push('/instructor/register')}
          className="w-full py-4 rounded-2xl font-bold text-base transition-all active:scale-[0.98]"
          style={{ background: blue, color: '#fff', letterSpacing: '-0.01em' }}
        >
          DM 노가다 끝내고 무료 시작하기 →
        </button>
        <p style={{ font: '400 12px/18px sans-serif', color: sub, textAlign: 'center', marginTop: 10 }}>
          심사 없음 · 첫 투어 무료 · 수수료 0%
        </p>
      </section>

      {/* ━━ 핵심 가치 3가지 ━━ */}
      <section className="px-5 mb-10 max-w-xl mx-auto">
        <div className="space-y-3">
          {VALUES.map(v => (
            <div key={v.title} className="flex items-start gap-4 rounded-2xl p-4"
              style={{ background: card, border: `1px solid ${border}` }}>
              <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                style={{ background: blueDim }}>
                {v.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p style={{ font: '700 14px/1 sans-serif', color: ink, margin: '0 0 5px' }}>{v.title}</p>
                <p style={{ font: '400 13px/20px sans-serif', color: sub, margin: 0 }}>{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ━━ BEFORE / AFTER ━━ */}
      <section className="px-5 mb-10 max-w-xl mx-auto">

        {/* 섹션 제목 */}
        <h2 style={{ font: '700 18px/26px sans-serif', color: ink, margin: '0 0 14px', letterSpacing: '-0.03em' }}>
          지금 강사님의 하루
        </h2>

        <div className="rounded-2xl p-5 mb-3"
          style={{ background: isDark ? '#1a0e0e' : '#fff1f1', border: `1px solid ${isDark ? '#3d1515' : '#fecdd3'}` }}>
          <p style={{ font: '600 11px/1 sans-serif', color: isDark ? '#f87171' : '#e11d48', marginBottom: 14, letterSpacing: '0.05em' }}>
            카톡 DM 예약의 현실
          </p>
          <div className="space-y-3">
            {DM_PAINS.map(item => (
              <div key={item.text} className="flex items-start gap-3">
                <span style={{ fontSize: 15, lineHeight: '20px', flexShrink: 0 }}>{item.emoji}</span>
                <span style={{ font: '400 13px/20px sans-serif', color: isDark ? '#fca5a5' : '#7f1d1d' }}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 화살표 */}
        <div className="flex justify-center my-3">
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: blue }}>
            <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
              <path d="M6 1.5v9M2.5 7l3.5 3.5L9.5 7" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        <div className="rounded-2xl p-5"
          style={{ background: isDark ? '#091a10' : '#f0fdf4', border: `1px solid ${isDark ? '#14532d' : '#bbf7d0'}` }}>
          <p style={{ font: '600 11px/1 sans-serif', color: isDark ? '#4ade80' : '#16a34a', marginBottom: 14, letterSpacing: '0.05em' }}>
            오늘다이빙 쓰면
          </p>
          <div className="space-y-3">
            {DM_AFTERS.map(item => (
              <div key={item.text} className="flex items-start gap-3">
                <span style={{ fontSize: 15, lineHeight: '20px', flexShrink: 0 }}>{item.emoji}</span>
                <span style={{ font: '400 13px/20px sans-serif', color: isDark ? '#86efac' : '#14532d' }}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━ "폼생폼사" 시각화 ━━ */}
      <section className="px-5 mb-10 max-w-xl mx-auto">
        <h2 style={{ font: '700 18px/26px sans-serif', color: ink, margin: '0 0 6px', letterSpacing: '-0.03em' }}>
          수강생 눈에는 이렇게 보입니다
        </h2>
        <p style={{ font: '400 13px/20px sans-serif', color: sub, margin: '0 0 14px' }}>
          같은 강사님, 링크를 눌렀을 때 어느 쪽이 더 믿음직스럽나요?
        </p>

        <div className="grid grid-cols-2 gap-3">
          {/* 링크트리 */}
          <div className="rounded-2xl p-4" style={{ background: isDark ? '#1a1210' : '#fef9f0', border: `1px solid ${isDark ? '#3d2a10' : '#fed7aa'}` }}>
            <p style={{ font: '600 11px/1 sans-serif', color: isDark ? '#fb923c' : '#c2410c', margin: '0 0 12px', letterSpacing: '0.03em' }}>
              링크트리 / 카톡
            </p>
            <div className="space-y-2">
              {[
                '🔗 링크 버튼 5개',
                '📝 구글폼 (허접)',
                '💸 계좌이체 후 카톡',
                '❓ 잔여석 모름',
                '📋 자격증? 직접 올림',
              ].map(t => (
                <p key={t} style={{ font: '400 12px/18px sans-serif', color: isDark ? '#fb923c' : '#9a3412', margin: 0 }}>{t}</p>
              ))}
            </div>
          </div>

          {/* 오늘다이빙 */}
          <div className="rounded-2xl p-4" style={{ background: isDark ? '#091a10' : '#f0fdf4', border: `1px solid ${isDark ? '#14532d' : '#bbf7d0'}` }}>
            <p style={{ font: '600 11px/1 sans-serif', color: isDark ? '#4ade80' : '#16a34a', margin: '0 0 12px', letterSpacing: '0.03em' }}>
              오늘다이빙 프로필
            </p>
            <div className="space-y-2">
              {[
                '🏅 AIDA 공식 인증 뱃지',
                '📅 실시간 잔여석 3/8',
                '💳 카드 결제 + 예약 확정',
                '📋 정보 자동 취합',
                '⭐ 수강생 후기 시스템',
              ].map(t => (
                <p key={t} style={{ font: '400 12px/18px sans-serif', color: isDark ? '#86efac' : '#14532d', margin: 0 }}>{t}</p>
              ))}
            </div>
          </div>
        </div>

        <p style={{ font: '500 13px/20px sans-serif', color: sub, textAlign: 'center', marginTop: 14 }}>
          "이 강사님은 시스템을 갖춘 <span style={{ color: blueText }}>프로구나</span>" — 수강생의 첫인상
        </p>
      </section>

      {/* ━━ HOW: 3단계 ━━ */}
      <section className="px-5 mb-10 max-w-xl mx-auto">
        <h2 style={{ font: '700 18px/26px sans-serif', color: ink, margin: '0 0 16px', letterSpacing: '-0.03em' }}>
          설정 3단계, 이후는 자동
        </h2>
        <div className="space-y-3">
          {STEPS.map(step => (
            <div key={step.num} className="flex items-start gap-4 rounded-2xl p-4"
              style={{ background: card, border: `1px solid ${border}` }}>
              <div className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center"
                style={{ background: blueDim }}>
                <span style={{ font: '700 14px/1 sans-serif', color: blueText }}>{step.num}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p style={{ font: '600 14px/1 sans-serif', color: ink, margin: 0 }}>{step.title}</p>
                  <span className="rounded-full px-2 py-0.5 flex-shrink-0"
                    style={{ font: '600 10px/16px sans-serif', color: blue, background: blueDim }}>
                    {step.badge}
                  </span>
                </div>
                <p style={{ font: '400 13px/20px sans-serif', color: sub, margin: 0 }}>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ━━ 수수료 비교 (보너스 섹션) ━━ */}
      <section className="px-5 mb-10 max-w-xl mx-auto">
        <div className="rounded-2xl p-4 flex items-center gap-4"
          style={{ background: isDark ? '#161f2e' : '#f7f8fa', border: `1px solid ${border}` }}>
          <div className="text-2xl flex-shrink-0">💡</div>
          <div className="flex-1 min-w-0">
            <p style={{ font: '600 13px/20px sans-serif', color: ink, margin: '0 0 3px' }}>
              타 플랫폼 사용 중이신가요?
            </p>
            <p style={{ font: '400 12px/18px sans-serif', color: sub, margin: 0 }}>
              수수료 20% → 건당 9,900원. 투어 10건이면{' '}
              <span style={{ color: blueText, fontWeight: 600 }}>최대 300만원 절약</span>
            </p>
          </div>
        </div>
      </section>

      {/* ━━ RISK REVERSAL ━━ */}
      <section className="px-5 mb-10 max-w-xl mx-auto">
        <div className="rounded-2xl p-6" style={{ background: blueDim, border: `1px solid ${isDark ? '#1e3a5f' : '#bfdbfe'}` }}>
          <p style={{ font: '700 17px/24px sans-serif', color: ink, margin: '0 0 8px', letterSpacing: '-0.03em' }}>
            첫 투어는 공짜로 써보세요
          </p>
          <p style={{ font: '400 14px/22px sans-serif', color: sub, margin: '0 0 18px' }}>
            카톡이랑 얼마나 다른지 한 번만 경험해보면 됩니다.<br />
            마음에 안 들면 그냥 안 쓰면 됩니다. 리스크 제로.
          </p>
          <button
            onClick={() => router.push('/instructor/register')}
            className="w-full py-3.5 rounded-xl font-bold text-sm text-white transition-all active:scale-[0.98]"
            style={{ background: blue, letterSpacing: '-0.01em' }}
          >
            첫 투어 무료로 시작하기 →
          </button>
        </div>
      </section>

      {/* ━━ FAQ ━━ */}
      <section className="px-5 mb-10 max-w-xl mx-auto">
        <h2 style={{ font: '700 18px/26px sans-serif', color: ink, margin: '0 0 14px', letterSpacing: '-0.03em' }}>
          자주 묻는 질문
        </h2>
        <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${border}` }}>
          {FAQ.map((item, i) => (
            <div key={item.q} className="px-5 py-4"
              style={{ borderTop: i > 0 ? `1px solid ${border}` : 'none', background: bg }}>
              <p style={{ font: '600 13px/20px sans-serif', color: ink, margin: '0 0 4px' }}>
                {item.q}
              </p>
              <p style={{ font: '400 13px/20px sans-serif', color: sub, margin: 0 }}>{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ━━ 하단 CTA ━━ */}
      <section className="px-5 pb-24 max-w-xl mx-auto">
        <div className="rounded-2xl p-6" style={{ background: isDark ? '#161f2e' : '#0a1929' }}>
          <p style={{ font: '700 20px/28px sans-serif', color: '#fff', margin: '0 0 8px', letterSpacing: '-0.04em' }}>
            DM 노가다는 오늘로 끝.
          </p>
          <p style={{ font: '400 14px/22px sans-serif', color: 'rgba(255,255,255,0.55)', margin: '0 0 22px' }}>
            예약금 결제부터 수강생 명단 자동 취합까지.<br />
            강사님은 <span style={{ color: '#93c5fd', fontWeight: 600 }}>강의에만 집중하세요.</span>
          </p>
          <button
            onClick={() => router.push('/instructor/register')}
            className="w-full py-4 rounded-xl font-bold text-base text-white transition-all active:scale-[0.98]"
            style={{ background: blue, letterSpacing: '-0.01em' }}
          >
            지금 바로 무료 시작하기 →
          </button>
          <p style={{ font: '400 12px/18px sans-serif', color: 'rgba(255,255,255,0.3)', textAlign: 'center', marginTop: 10 }}>
            심사 없음 · 첫 투어 무료 · 수수료 0%
          </p>
        </div>
      </section>

    </div>
  )
}
